import { useMemo } from 'react'
import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from 'recharts'

import { ExportButtonV2 } from '@/catalog-apps/components/component-library/DataTableV2/components/ExportButtonV2'
import { DataTableV2DataRow } from '@/catalog-apps/components/component-library/DataTableV2/types'
import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/shadcn/charts'

import { AiRadarChartSchema } from './types-ai-radar-chart'

/** Matches `--chart-1` ... `--chart-5` on `:root` and `.dark` in `index.scss`. */
const THEME_CHART_COLOR_SLOT_COUNT = 5

const DEFAULT_MIN_CHART_HEIGHT = 320

function themeChartFillHsl(seriesIndex: number): string {
  const slot = (seriesIndex % THEME_CHART_COLOR_SLOT_COUNT) + 1
  return `hsl(var(--chart-${slot}))`
}

export function AiRadarChart({ schema }: { schema: AiRadarChartSchema }) {
  const exportData = useMemo<DataTableV2DataRow[]>(
    () =>
      schema.data.map((item, index) => {
        const exportRow: DataTableV2DataRow = {
          id: index + 1,
          [schema.config.categoryKey]: item[schema.config.categoryKey],
        }

        schema.config.radarDataKeys.forEach((key) => {
          const seriesLabel = schema.config.seriesConfig[key]?.label ?? key
          exportRow[seriesLabel] = item[key]
        })

        return exportRow
      }),
    [schema]
  )

  return (
    <div className="flex h-[50vh] w-full flex-col overflow-hidden">
      <ComponentHeader emphasize wrapperClassName="rounded-b-none">
        <ComponentTitle title={schema.title} description={schema.description} />
        <ComponentActionsContainer className="bg-transparent p-0">
          <ExportButtonV2
            data={exportData}
            title={schema.title}
            selectionRequired={false}
            className="text-background dark:text-foreground"
          />
        </ComponentActionsContainer>
      </ComponentHeader>
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-background">
        <ChartContainer
          className="mx-auto aspect-auto h-full w-full min-h-0"
          config={schema.config.seriesConfig}
          style={{ minHeight: schema.config.minChartHeight ?? DEFAULT_MIN_CHART_HEIGHT }}
        >
          <RadarChart
            accessibilityLayer
            data={schema.data}
            layout={schema.config.layout ?? 'centric'}
            startAngle={schema.config.startAngle ?? 90}
            endAngle={schema.config.endAngle ?? -270}
            innerRadius={schema.config.innerRadius ?? 0}
            outerRadius={schema.config.outerRadius ?? '80%'}
            cx={schema.config.cx ?? '50%'}
            cy={schema.config.cy ?? '50%'}
            margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
          >
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            <PolarAngleAxis dataKey={schema.config.categoryKey} />
            {schema.config.showGrid && schema.config.gridType !== 'none' && (
              <PolarGrid gridType={schema.config.gridType ?? 'polygon'} />
            )}
            {schema.config.showLegend && <ChartLegend content={<ChartLegendContent />} />}
            {schema.config.radarDataKeys.map((key, seriesIndex) => {
              const radarConfig = schema.config.seriesConfig[key as keyof typeof schema.config.seriesConfig]
              const color = radarConfig?.color ?? themeChartFillHsl(seriesIndex)

              return (
                <Radar
                  key={key}
                  dataKey={key}
                  name={radarConfig?.label ?? key}
                  fill={color}
                  fillOpacity={radarConfig?.fillOpacity ?? 0.25}
                  stroke={schema.config.showOutline ? color : undefined}
                  strokeWidth={schema.config.showOutline ? radarConfig?.strokeWidth ?? 2 : 0}
                />
              )
            })}
          </RadarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
